/**
 * Primary hook for ViewJob to access all Ghostwriter-related data.
 *
 * Encapsulates:
 * - Proctor gating (rn_ghostwriter_test must be active)
 * - Eligibility lookup (from the shared autoAppliableJobKeys set)
 * - Application data fetch (always re-fetches on every render)
 * - Consent status resolution (via useResolveConsentStatus)
 * - Button state computation for ViewJob CTA rendering
 *
 * ViewJob-first scenario: if the eligibility set hasn't been hydrated
 * by Homepage yet, falls back to a single-job eligibility + application
 * fetch.
 *
 * Phase A / Phase B variant: the payload's `jobKeys` is the
 * eligibility source (skip the backend check); both consent-exempt.
 *
 * Phase E variant: same flow as phase_b, but eligibility comes from a real-time
 * per-job ViewJob read (the payload is a traffic-cap allowlist, not the source
 * of truth). Both verdicts are cached per session, so a job is read once.
 *
 * Two-step by design (do not collapse): the eligibility read only adds the job
 * key to the auto-appliable set — it does NOT fetch the application. Adding the
 * key flips this effect's `hydrated`/`isAutoAppliable` deps, so on re-entry the
 * `hydrated && isAutoAppliable` branch fetches the application for the button's
 * text state. Net: an ineligible job never fetches an application, and an
 * eligible one fetches it exactly once via that branch — fetching it in the
 * phase_e branch too would double-fetch. The application fetch is off the CTA
 * critical path (the CTA already shows on eligibility), so it doesn't delay
 * render.
 *
 * Consent optimization: the consent check is gated on eligibility, so
 * ineligible jobs never trigger the consent network call.
 */

import { loadingAsyncState, successAsyncState, errorAsyncState } from "./AsyncState";
import { useGhostwriterApi } from "./ghostwriterApi";
import {
  useIsJobAutoAppliable,
  useIsJobInPhaseEAllowlist,
  useIsJobPhaseEIneligible,
  useGhostwriterJobByKey,
} from "./GhostwriterContext";
import {
  addJobToStore,
  addJobKeyToAutoAppliableSet,
  addJobKeyToPhaseEIneligibleSet,
  markPhaseEReadInFlight,
  isPhaseEReadInFlight,
  clearPhaseEReadInFlight,
  setAutoAppliableJobKeys,
  setJobsState,
  removeJobFromStore,
  getSnapshot,
} from "./GhostwriterStore";
import { useGhostwriterGate } from "./useGhostwriterGate";
import { useResolveConsentStatus } from "./useResolveConsentStatus";
import { useMinDurationLoading } from "../hooks/useMinDurationLoading";
import {
  type ScheduleApplyJob,
  type ScheduleApplyStatus,
  ScheduleApplyStatuses,
  ScheduleApplyStatusSeverity,
} from "../models/ScheduleApplyJob";

import { useCallback, useEffect, useMemo, useRef } from "react";

export type GhostwriterButtonAction =
  | "none"
  | "loading"
  | "try-auto-apply"
  | "auto-apply"
  | "in-progress"
  | "ready-for-review"
  | "applied"
  | "error";

export const GHOSTWRITER_PRIMARY_ACTIONS: GhostwriterButtonAction[] = [
  "try-auto-apply",
  "auto-apply",
  "in-progress",
  "ready-for-review",
  "applied",
];

export function shouldShowSecondaryApply(buttonAction: GhostwriterButtonAction): boolean {
  return buttonAction !== "applied" && GHOSTWRITER_PRIMARY_ACTIONS.includes(buttonAction);
}

const STATUS_ACTION_MAP = new Map<ScheduleApplyStatus, GhostwriterButtonAction>([
  [ScheduleApplyStatuses.NotStarted, "auto-apply"],
  [ScheduleApplyStatuses.AutoApplyCancelled, "auto-apply"],
  [ScheduleApplyStatuses.AnalyzingJob, "in-progress"],
  [ScheduleApplyStatuses.FillingOutApplication, "in-progress"],
  [ScheduleApplyStatuses.SubmittingApplication, "in-progress"],
  [ScheduleApplyStatuses.RetryPendingCaptcha, "in-progress"],
  [ScheduleApplyStatuses.ReadyToReview, "ready-for-review"],
  [ScheduleApplyStatuses.MissingRequiredInfo, "ready-for-review"],
  [ScheduleApplyStatuses.Applied, "applied"],
  [ScheduleApplyStatuses.AutoApplyFailed, "error"],
  [ScheduleApplyStatuses.SubmissionFailed, "error"],
]);

function computeButtonAction(
  isEnabled: boolean | null,
  isAutoAppliable: boolean,
  hasConsented: boolean,
  consentLoading: boolean,
  ghostwriterJob: ScheduleApplyJob | null,
  appliableLoading: boolean,
  consentExempt: boolean,
): GhostwriterButtonAction {
  if (isEnabled !== true) return "none";
  if (appliableLoading) return "loading";
  if (!isAutoAppliable) return "none";
  // Consent-exempt buckets (phase_a/b/e) show the CTA on eligibility alone
  // while consent resolves in the background.
  if (!consentExempt && consentLoading) return "loading";
  if (!hasConsented) return "try-auto-apply";
  if (!ghostwriterJob) return "auto-apply";

  const { applicationStatus } = ghostwriterJob;
  return (
    STATUS_ACTION_MAP.get(applicationStatus) ??
    (applicationStatus.severity === ScheduleApplyStatusSeverity.Error ? "error" : "in-progress")
  );
}

export interface GhostwriterViewJobResult {
  buttonAction: GhostwriterButtonAction;
  applicationId: string | null;
  /**
   * True if the user is in the phase A (synchronous flow) bucket.
   * Consumers branch CTA copy on this flag — see RN-3662.
   */
  isPhaseA: boolean;
  /**
   * True if the user is in the phase B bucket (payload fast-path). phase_b
   * now shares phase_c's synchronous Prefill UI flow on ViewJob. See RN-5091.
   */
  isPhaseB: boolean;
  /**
   * True if the user is in the phase C bucket — phase A's Prefill flow with
   * the standard 'Auto apply' / 'Try auto apply' CTA copy. ViewJob opens the
   * prefilling modal for phase A, phase B, or phase C. See RN-5091.
   */
  isPhaseC: boolean;
  /** True if in the phase E bucket. Same routing/CTA as phase_b; consumers gate
   * the synchronous Prefill flow on it alongside phase_b/phase_c. */
  isPhaseE: boolean;
  isAutoApplyLoading: boolean;
  beginAutoApplyLoading: () => void;
  /**
   * True when the user should get phase A's "Prefill" ViewJob experience
   * (CTA copy, prefilling modal entry). Phase D is a phase-A clone that
   * also adds the in-review intervention screen, so on ViewJob it must
   * look identical to phase A. Prefer this over `isPhaseA` for any
   * Prefill-experience branch. See RN-3367.
   */
  usesPrefillExperience: boolean;
  startAutoApply: () => Promise<void>;
  stopAutoApply: () => void;
  /**
   * Re-fetches the ghostwriter application for this job and writes it
   * into the shared store. Used when the prefilling modal dismisses so
   * ViewJob can show the latest CTA state.
   */
  refreshApplication: () => Promise<void>;
}

export function useGhostwriterForViewJob(jobKey: string | null): GhostwriterViewJobResult {
  const { isEnabled, isPhaseA, isPhaseB, isPhaseC, isPhaseD, isPhaseE } = useGhostwriterGate();
  const {
    fetchAutoAppliableJobKeys,
    fetchApplicationForJob,
    fetchEligibilityAndApplication,
    startScheduleApply,
  } = useGhostwriterApi();
  const { isAutoAppliable, loading: appliableLoading, hydrated } = useIsJobAutoAppliable(jobKey);
  // phase_e: the allowlist caps which jobs run the real-time read.
  const isInPhaseEAllowlist = useIsJobInPhaseEAllowlist(jobKey);
  // phase_e: a job already read as ineligible this session skips the re-read.
  const isPhaseEIneligible = useIsJobPhaseEIneligible(jobKey);
  const { job: ghostwriterJob } = useGhostwriterJobByKey(jobKey);
  // Ineligible jobs never trigger the consent network call.
  const shouldResolveConsent = isEnabled === true && isAutoAppliable;
  const { hasConsented, consentLoading } = useResolveConsentStatus(shouldResolveConsent);

  // Bumped on each read so stale responses can be ignored
  const applicationReadGenerationRef = useRef(0);

  useEffect(() => {
    if (!isEnabled || !jobKey) return;

    if (hydrated && isAutoAppliable) {
      // Also serves phase_e: once its eligibility read flips `isAutoAppliable`,
      // this branch fetches the application on re-entry.
      const generation = ++applicationReadGenerationRef.current;
      setJobsState(loadingAsyncState(getSnapshot().jobs.data));
      fetchApplicationForJob(jobKey)
        .then((job) => {
          if (applicationReadGenerationRef.current !== generation) return;
          if (job) {
            addJobToStore(job);
          } else {
            // Application no longer exists, drop the stale entry
            removeJobFromStore(jobKey);
          }
        })
        .catch((err: Error) => {
          if (applicationReadGenerationRef.current !== generation) return;
          setJobsState(errorAsyncState(err, getSnapshot().jobs.data));
        });
    } else if (
      isPhaseE === true &&
      isInPhaseEAllowlist &&
      !appliableLoading &&
      !isPhaseEIneligible &&
      !isPhaseEReadInFlight(jobKey)
    ) {
      // Eligibility-only read; branch 1 fetches the app on re-entry once this
      // flips `isAutoAppliable` (fetching here too would double-fetch). Mark it
      // in flight (non-reactive set, not the loading slice which loops on error).
      markPhaseEReadInFlight(jobKey);
      fetchAutoAppliableJobKeys([jobKey])
        .then((keys) => {
          if (keys.includes(jobKey)) {
            addJobKeyToAutoAppliableSet(jobKey);
          } else {
            addJobKeyToPhaseEIneligibleSet(jobKey);
          }
        })
        // Error goes to the eligibility slice; not cached as ineligible, so it retries.
        .catch((err: Error) =>
          setAutoAppliableJobKeys(errorAsyncState(err, getSnapshot().autoAppliableJobKeys.data)),
        )
        .finally(() => clearPhaseEReadInFlight(jobKey));
    } else if (!appliableLoading && !isPhaseA && !isPhaseB && isPhaseE !== true) {
      // phase_a/phase_b trusts its payload; phase_e uses the eligibility-only read
      // above. All other enabled buckets fall back to the combined read.
      const generation = ++applicationReadGenerationRef.current;
      setJobsState(loadingAsyncState(getSnapshot().jobs.data));
      fetchEligibilityAndApplication(jobKey)
        .then((result) => {
          if (applicationReadGenerationRef.current !== generation) return;
          if (result.eligible) {
            addJobKeyToAutoAppliableSet(jobKey);
            if (result.application) {
              addJobToStore(result.application);
            } else {
              // Eligible but the application is gone, clear the stale entry
              removeJobFromStore(jobKey);
            }
          } else {
            setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
          }
        })
        .catch((err: Error) => {
          if (applicationReadGenerationRef.current !== generation) return;
          setJobsState(errorAsyncState(err, getSnapshot().jobs.data));
        });
    }

    // Bump on teardown too, so a read still in flight at unmount is ignored
    return () => {
      applicationReadGenerationRef.current += 1;
    };
  }, [
    isEnabled,
    isPhaseA,
    isPhaseB,
    isPhaseE,
    isInPhaseEAllowlist,
    isPhaseEIneligible,
    jobKey,
    hydrated,
    isAutoAppliable,
    appliableLoading,
    fetchAutoAppliableJobKeys,
    fetchApplicationForJob,
    fetchEligibilityAndApplication,
  ]);

  const {
    isLoading: isAutoApplyLoading,
    start: beginAutoApplyLoading,
    stop: stopAutoApply,
  } = useMinDurationLoading();

  const startAutoApply = useCallback(async () => {
    if (!jobKey) throw new Error("Cannot start auto apply without a job key");
    beginAutoApplyLoading();
    try {
      await startScheduleApply(jobKey);
    } catch (err) {
      stopAutoApply();
      throw err;
    }
  }, [jobKey, startScheduleApply, beginAutoApplyLoading, stopAutoApply]);

  const refreshApplication = useCallback(async () => {
    if (!jobKey) return;

    const generation = ++applicationReadGenerationRef.current;
    try {
      const job = await fetchApplicationForJob(jobKey);
      if (applicationReadGenerationRef.current !== generation) return;
      if (job) {
        addJobToStore(job);
      } else {
        // Application no longer exists, drop the stale entry
        removeJobFromStore(jobKey);
      }
    } catch (err) {
      console.warn("[useGhostwriterForViewJob] refreshApplication failed", err);
    }
  }, [jobKey, fetchApplicationForJob]);

  const buttonAction = useMemo(() => {
    // phase_a, phase_b, and phase_e don't block the CTA on consent (see
    // computeButtonAction).
    const consentExempt = isPhaseA === true || isPhaseB === true || isPhaseE === true;
    return computeButtonAction(
      isEnabled,
      isAutoAppliable,
      hasConsented,
      consentLoading,
      ghostwriterJob,
      appliableLoading,
      consentExempt,
    );
  }, [
    isEnabled,
    isAutoAppliable,
    hasConsented,
    consentLoading,
    ghostwriterJob,
    appliableLoading,
    isPhaseA,
    isPhaseB,
    isPhaseE,
  ]);

  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    stopAutoApply();
  }, [buttonAction, stopAutoApply]);

  return {
    buttonAction,
    applicationId: ghostwriterJob?.applicationId ?? null,
    isPhaseA: isPhaseA === true,
    isPhaseB: isPhaseB === true,
    isPhaseC: isPhaseC === true,
    isPhaseE: isPhaseE === true,
    isAutoApplyLoading,
    beginAutoApplyLoading,
    usesPrefillExperience: isPhaseA === true || isPhaseD === true,
    startAutoApply,
    stopAutoApply,
    refreshApplication,
  };
}
